'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { notifyEnrollmentActionFailure } from './enrollment-action-feedback';
import {
  mapEnrollmentDeploymentSecrets,
  type InternalNetworkEnrollmentSecrets,
} from './internal-connection.model';

export type InternalNetworkSensorEnrollmentCallbacks = {
  onCreateEnrollment: () => Promise<InternalNetworkEnrollmentSecrets>;
  onRotateEnrollmentKey: (
    enrollmentId: string,
  ) => Promise<InternalNetworkEnrollmentSecrets>;
  onRevokeEnrollment: (enrollmentId: string) => Promise<unknown>;
};

export type InternalNetworkSecretsDialogState = {
  title: string;
  secrets: InternalNetworkEnrollmentSecrets;
};

export type InternalNetworkSensorEnrollmentActions = {
  secretsDialog: InternalNetworkSecretsDialogState | null;
  isCreating: boolean;
  rotatingEnrollmentId: string | null;
  revokingEnrollmentId: string | null;
  pendingRotateEnrollmentId: string | null;
  createEnrollment: () => Promise<void>;
  requestRotateEnrollmentKey: (enrollmentId: string) => void;
  confirmRotateEnrollmentKey: () => Promise<void>;
  cancelRotateEnrollmentKey: () => void;
  revokeEnrollment: (enrollmentId: string) => Promise<void>;
  closeSecretsDialog: () => void;
};

export function useInternalNetworkSensorEnrollmentActions({
  onCreateEnrollment,
  onRotateEnrollmentKey,
  onRevokeEnrollment,
}: InternalNetworkSensorEnrollmentCallbacks): InternalNetworkSensorEnrollmentActions {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.enrollments',
  );
  const tCommon = useTranslations('designSystem.common');
  const [secretsDialog, setSecretsDialog] =
    useState<InternalNetworkSecretsDialogState | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [rotatingEnrollmentId, setRotatingEnrollmentId] = useState<
    string | null
  >(null);
  const [revokingEnrollmentId, setRevokingEnrollmentId] = useState<
    string | null
  >(null);
  const [pendingRotateEnrollmentId, setPendingRotateEnrollmentId] = useState<
    string | null
  >(null);

  const createEnrollment = async () => {
    setIsCreating(true);

    try {
      const result = await onCreateEnrollment();

      setSecretsDialog({
        title: t('createSuccessTitle'),
        secrets: mapEnrollmentDeploymentSecrets(result),
      });
      toasterApi.success({ title: t('createSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'create internal network sensor enrollment',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const confirmRotateEnrollmentKey = async () => {
    if (!pendingRotateEnrollmentId) {
      return;
    }

    setRotatingEnrollmentId(pendingRotateEnrollmentId);

    try {
      const result = await onRotateEnrollmentKey(pendingRotateEnrollmentId);

      setPendingRotateEnrollmentId(null);
      setSecretsDialog({
        title: t('regenerateSuccessTitle'),
        secrets: mapEnrollmentDeploymentSecrets(result),
      });
      toasterApi.success({ title: t('regenerateSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'rotate internal network sensor enrollment key',
      });
    } finally {
      setRotatingEnrollmentId(null);
    }
  };

  const revokeEnrollment = async (enrollmentId: string) => {
    setRevokingEnrollmentId(enrollmentId);

    try {
      await onRevokeEnrollment(enrollmentId);
      toasterApi.success({ title: t('revokeSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'revoke internal network sensor enrollment',
      });
    } finally {
      setRevokingEnrollmentId(null);
    }
  };

  return {
    secretsDialog,
    isCreating,
    rotatingEnrollmentId,
    revokingEnrollmentId,
    pendingRotateEnrollmentId,
    createEnrollment,
    requestRotateEnrollmentKey: setPendingRotateEnrollmentId,
    confirmRotateEnrollmentKey,
    cancelRotateEnrollmentKey: () => setPendingRotateEnrollmentId(null),
    revokeEnrollment,
    closeSecretsDialog: () => setSecretsDialog(null),
  };
}
